import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeader from './SectionHeader';

const testimonials = [
  {
    id: 1,
    name: 'Rajesh Sharma',
    role: 'Homeowner, Rameshwar Dham',
    text: 'Avdhoot Group delivered beyond our expectations. The quality of construction and attention to detail in our apartment is truly remarkable. Living here feels like a luxury experience every day.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
  },
  {
    id: 2,
    name: 'Priya Patel',
    role: 'Investor, Dev Vatika',
    text: 'I have invested in multiple projects by Avdhoot Group and every time they have delivered on time with exceptional quality. Their transparency and professionalism set them apart.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
  },
  {
    id: 3,
    name: 'Amit Jain',
    role: 'Homeowner, Kesar Bagh',
    text: 'Our home at Kesar Bagh has been a dream come true. The premium location, modern design, and excellent maintenance make it the perfect living address.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
  },
];

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const next = useCallback(() => setCurrent((prev) => (prev + 1) % testimonials.length), []);
  const prev = () => setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  // Auto-play with 5s interval, pause on hover
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [isPaused, next]);

  return (
    <section
      className="relative py-28 lg:py-32 bg-charcoal text-white overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Line pattern overlay */}
      <div className="absolute inset-0 line-pattern" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <SectionHeader
            label="Testimonials"
            title="What Our Clients Say"
            light
          />
        </div>

        <div className="max-w-3xl mx-auto relative">
          {/* Large decorative quote */}
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-9xl font-serif text-gold/10 leading-none select-none">
            &ldquo;
          </span>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-center pt-12"
            >
              {/* Profile Image */}
              <div className="w-20 h-20 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold/30 ring-offset-4 ring-offset-charcoal">
                <img
                  src={testimonials[current].image}
                  alt={testimonials[current].name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Stars */}
              <div className="flex justify-center mb-6 gap-1">
                {[...Array(testimonials[current].rating)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              <p className="text-lg sm:text-xl text-cream-200/80 font-sans leading-relaxed mb-10 italic">
                "{testimonials[current].text}"
              </p>

              <div>
                <p className="text-white font-serif text-xl font-semibold">
                  {testimonials[current].name}
                </p>
                <p className="text-gold text-xs font-sans uppercase tracking-widest-plus mt-1">
                  {testimonials[current].role}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation — Line Indicators */}
          <div className="flex justify-center items-center mt-12 gap-6">
            <button
              onClick={prev}
              className="w-10 h-10 border border-gold/20 hover:border-gold text-gold/40 hover:text-gold flex items-center justify-center transition-all duration-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-0.5 rounded-full transition-all duration-500 ease-luxury ${
                    i === current ? 'w-8 bg-gold' : 'w-4 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-10 h-10 border border-gold/20 hover:border-gold text-gold/40 hover:text-gold flex items-center justify-center transition-all duration-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
